import type { JSX } from 'react';
import { Route, Routes } from 'react-router';
import { ActivityPage } from '../activity/ActivityPage';
import { DocumentsPage } from '../documents/DocumentsPage';
import { AppHost } from '../host/AppHost';
import { ProfilePage } from '../me/ProfilePage';
import { TeacherSpaceMount } from '../teacher/TeacherSpaceMount';
import { WindowProvider } from './context';
import type { WindowFrame } from './context';
import type { DeskWindow } from './stack';

/**
 * Une fenêtre de la pile, avec **ses** routes à elle.
 *
 * C'est la pièce qui rend le multi-fenêtres possible sans réécrire les trois pages hébergées.
 * Chacune joue le même tableau de routes que le bureau jouait autrefois une seule fois, mais
 * contre l'adresse de SA fenêtre (`<Routes location={win.path}>`) et non contre celle du
 * navigateur. `useParams` continue donc de fonctionner exactement comme avant dans `AppHost`
 * (`:appId`, sous-routes de l'app) et dans `TeacherSpace` (`:section`, `:userId`) : elles ne
 * savent pas qu'elles sont plusieurs. `/activity` (la consigne du jour) n'a pas de sous-route :
 * c'est un écran, pas un espace.
 *
 * L'alternative — passer la section et l'identifiant en propriétés — aurait demandé de démonter
 * le routage interne de l'espace enseignant et celui de chaque app, pour le même résultat.
 *
 * `key={win.key}` (posé par `Desk`) fait le reste : tant qu'une fenêtre est ouverte, React garde
 * son sous-arbre **monté**. Une app conserve donc son état, son défilement et sa position quand
 * une autre passe devant, et une fenêtre réduite ne perd pas le travail en cours — elle est juste
 * cachée (`AppWindow`, `display: none`).
 */
export interface WindowSlotProps {
  win: DeskWindow;
  frame: WindowFrame;
}

export function WindowSlot({ win, frame }: WindowSlotProps): JSX.Element {
  return (
    <WindowProvider value={frame}>
      <Routes location={win.path}>
        <Route path="/app/:appId/*" element={<AppHost />} />
        <Route path="/me" element={<ProfilePage />} />
        <Route path="/activity" element={<ActivityPage />} />
        {/* « Documents » — l'entrée du §5 du cahier des charges. Comme `/activity` : un écran, pas
            un espace, donc aucune sous-route. */}
        <Route path="/documents" element={<DocumentsPage />} />
        <Route path="/teacher/student/:userId" element={<TeacherSpaceMount />} />
        <Route path="/teacher/:section" element={<TeacherSpaceMount />} />
      </Routes>
    </WindowProvider>
  );
}
